import {
  TeamMemberBulkButtonComponents,
  TeamMemberTableBulkName,
} from "@components/common/faro-table/faro-table-types";
import { BulkExportMembers } from "@components/table/members/bulk-actions/bulk-export-members";
import { BulkRemoveMember } from "@components/table/members/bulk-actions/bulk-remove-member";
import { BulkResendInvitation } from "@components/table/members/bulk-actions/bulk-resend-invitation";
import { BulkSendEmail } from "@components/table/members/bulk-actions/bulk-send-email";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { SdbTeamMemberBase } from "@custom-types/teams-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { isMemberActive } from "@utils/user-utils";
import { useMemo } from "react";

interface Props extends BaseCompanyIdProps {
  /** Selected team details */
  team: FaroCoreApiTypes.Team;

  /** List of members of the team to process in bulk actions */
  teamMembers: SdbTeamMemberBase[];
}

/** List all the bulk action components for team members table */
export function TeamMembersPageBulkActions({
  companyId,
  team,
  teamMembers,
}: Props): TeamMemberBulkButtonComponents {
  const selectedEntities = useAppSelector(selectedEntitiesSelector("members"));
  const { isFetchingTeamMembers } = useAppSelector(fetchingTeamsFlagsSelector);
  const isFetching = isFetchingTeamMembers === "pending";
  const isAnyRowSelected = selectedEntities.length !== 0;
  const isAnyMemberPending = selectedEntities.some(
    (member) => !isMemberActive(member)
  );
  const { canDeleteMembersFromTeam, canDeleteMemberFromCompany } =
    useHasUserValidRoleCompanyLevel();

  const allowedButtonsBasedOnRowSelection: TeamMemberTableBulkName[] =
    useMemo(() => {
      const allowedButtons: TeamMemberTableBulkName[] = ["export"];

      if (isAnyRowSelected) {
        allowedButtons.push("sendEmail");

        if (isAnyMemberPending) {
          allowedButtons.push("resendInvitation");
        }
        if (canDeleteMembersFromTeam && !team.category.isManaged) {
          allowedButtons.push("removeMember");
        }
        if (canDeleteMemberFromCompany) {
          allowedButtons.push("removeMemberFromWorkspace");
        }
      }

      return allowedButtons;
    }, [
      isAnyRowSelected,
      isAnyMemberPending,
      canDeleteMembersFromTeam,
      team.category.isManaged,
      canDeleteMemberFromCompany,
    ]);

  const { shouldShowBulkButtons } = useShouldShowBulkAction({
    allowedButtonsBasedOnRowSelection,
  });

  /** Determine whether to show the export button */
  const shouldShowExportButton =
    shouldShowBulkButtons("export") && (isFetching || teamMembers.length > 0);

  return {
    resendInvitation: shouldShowBulkButtons("resendInvitation") ? (
      <BulkResendInvitation
        selectedEntities={selectedEntities}
        companyId={companyId}
        subjectType="team"
      />
    ) : null,

    removeMember: shouldShowBulkButtons("removeMember") ? (
      <BulkRemoveMember
        subjectType="team"
        companyId={companyId}
        teamId={team.id}
        title="Remove from team"
      />
    ) : null,

    removeMemberFromWorkspace: shouldShowBulkButtons(
      "removeMemberFromWorkspace"
    ) ? (
      <BulkRemoveMember
        subjectType="workspaceTeam"
        companyId={companyId}
        title="Remove from Workspace"
        teamId={team.id}
      />
    ) : null,

    sendEmail: shouldShowBulkButtons("sendEmail") ? (
      <BulkSendEmail selectedEntities={selectedEntities} />
    ) : null,

    export: shouldShowExportButton ? (
      <BulkExportMembers
        selectedEntities={isAnyRowSelected ? selectedEntities : teamMembers}
        subjectType="team"
        entityName={team.name}
        areEntitiesSelected={isAnyRowSelected}
        teamId={team.id}
        companyId={companyId}
      />
    ) : null,
  };
}
